import { groupBy, orderBy } from 'lodash';
import moment from 'moment';
import { createRef, useContext, useEffect, useRef, useState } from "react";
import { FaBell, FaCheckCircle, FaCircle, FaRegCircle, FaTimes } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { fetchNotificationsByUserId, selectNotifications, subscribeUserNotification, unsubscribeUserNotifications, updateNotification } from "../common/slices/notification";
import theme from "../common/theme";
import { ErrorContext } from "../ErrorContext";
import { ButtonBase } from "./Buttons";
import { DropdownContainer, DropdownContent } from "./Dropdown";
import ListHeader from "./ListHeader";
import Row from "./Row";
import { H2, P, Pmb } from "./Typography";
import { useNavigate } from 'react-router-dom';
import * as _ from 'lodash';
import { toast } from 'react-toastify';
import { selectUser } from '../common/slices/auth';

const NotificationBell = styled.div`
    position: relative;
    z-index: 2;
    padding: 0 0 0 1rem;
    margin-right: 1rem;
    color: ${theme.palette.text};
    user-select: none;
    &:hover {
        opcacity: 0.6;
    }
    
    > span {
        z-index: 1;
        position: absolute;
        top: -14px;
        font-size: 0.9rem;
        background: ${theme.palette.text};
        color: ${theme.palette.light};
        border-radius: 3rem;
        border: 1px solid ${theme.palette.text};
        padding: 0.125rem;
    }

    `

const NotificationContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0.5rem;
    border-bottom: 1px solid ${theme.palette.border};
    
    &:hover {
        background: ${theme.palette.border};
    }
`

const NotificationContent = styled(DropdownContent)`
    padding: 1rem;
    display: block;
    max-height: calc(80vh);
    overflow-y: auto;
`

const Notification = ({ notification, ...rest }) => {
    return (
        <NotificationContainer {...rest}>
            <Row>
                {notification.hasViewed ? <FaCheckCircle style={{ fontSize: '14px' }} /> : <FaRegCircle style={{ fontSize: '14px' }} />}
                <div style={{ flex: 1 }}>
                    <Pmb style={{ color: theme.palette.borderDark, fontWeight: '400' }}>{moment(notification.date).format("h:mm a")} - {moment(notification.date).fromNow()}</Pmb>
                    <Pmb>{notification.subject}</Pmb>
                    <P dangerouslySetInnerHTML={{ __html: notification.body?.replaceAll("\n", "<br />") }}></P>
                </div>

            </Row>
        </NotificationContainer>
    )
}

let count = 0;
const Notifications = () => {
    const errorContext = useContext(ErrorContext);
    const dispatch = useDispatch();
    const [visible, setVisible] = useState(false);
    const notifications = useSelector(selectNotifications);
    const navigate = useNavigate();
    const user = useSelector(selectUser)

    const reloadData = async () => {
        try {
            await dispatch(fetchNotificationsByUserId());
        } catch (ex) {
            console.log(ex);
            errorContext.setError({ errorMessage: "Failed to get user notifications.", forSupport: ex.message });
        }
    }

    const onNotificationClick = async (notification) => {
        try {
            await dispatch(updateNotification({ id: notification.id, hasViewed: true, expiry: moment().add(15, 'minute').unix() }))
            const data = JSON.parse(notification.content);

            switch (data.alertType) {
                case "SIGNED_URL":
                    window.open(data.url, "_blank");
                    break;
                case "SHARE":
                    navigate(`/safety-forms/${data.id}`);
                    break;
                case "REPORT_EXPORT":
                    navigate(`/daily-report/${data.id}`);
                    break;
                default:
                    break;
            }
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to mark notification viewed", forSupport: ex.message });
        }
    }

    const hideMenu = () => setVisible(false);

    useEffect(() => {
        if (visible) {
            reloadData();
            window.addEventListener('click', hideMenu);

        }
        return () => window.removeEventListener('click', hideMenu);
    }, [visible]);

    useEffect(() => {
        reloadData();
    }, [])


    const unread = notifications.filter(n => !n.hasViewed);
    const byDay = groupBy(notifications, (n) => moment(n.date).dayOfYear())

    return (
        <DropdownContainer>
            <NotificationBell onClick={(e) => { e.stopPropagation(); setVisible(!visible) }}>
                <FaBell />
                {unread.length !== 0 && <span>{unread.length}</span>}
            </NotificationBell>

            {
                visible &&
                <NotificationContent onClick={(e) => e.stopPropagation()}>
                    <Row>
                        <H2 style={{ flex: 1 }}>Notifications</H2>
                        <ButtonBase onClick={() => setVisible(false)}><FaTimes /></ButtonBase>
                    </Row>
                    {orderBy(Object.entries(byDay), "[0]", 'desc').map(([day, notifications]) => {
                        const today = moment().dayOfYear();
                        return (
                            [
                                <ListHeader key={day}>{day == today ? 'Today' : moment(notifications[0].date).format("MMM DD, YYYY")}</ListHeader>,
                                orderBy(notifications, 'date', 'desc').map(notification => (
                                    <Notification
                                        key={notification.id}
                                        notification={notification}
                                        onClick={() => onNotificationClick(notification)}
                                    />
                                ))
                            ]
                        )

                    })}
                </NotificationContent>
            }
        </DropdownContainer >
    )
}

export default Notifications